'use client';

import Link from 'next/link';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { Profile } from '@hub/common/src/types/cit-public-api';

import { getName } from '@hub/common/src/properties';
import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';
import {
  addTestThemeToHref,
  getProfileInstanceLink,
  toUrlSafeSlug
} from '@hub/common/src/navigationUtils';

import PropertiesView from './PropertiesView';
import MediaViewer from './MediaViewer';

import LinkIcon from './SVG/LinkIcon';
import { useCitThemeClient } from './CitThemeProvider';
import { ReferenceOverlay, type ReferenceType } from './ReferenceOverlay';
import { CitOrigin } from '@hub/common/src/types/cit-public-api';

export default function ProfileDetailLayout({
  profile,
  accountId,
  closeButton,
  enableGallery = true,
  setButtonVisibility,
  buttonVisibility,
  isOverlay = false
}: {
  profile: Profile | null;
  accountId: string;
  closeButton?: React.ReactNode;
  enableGallery?: boolean;
  setButtonVisibility?: Dispatch<SetStateAction<'visible' | 'hidden' | 'locked'>>;
  buttonVisibility?: 'visible' | 'hidden' | 'locked';
  isOverlay?: boolean;
}) {
  const [selectedReference, setSelectedReference] = useState<ReferenceType | undefined>();

  const [prevButtonVisibility, setPrevButtonVisibility] = useState<'visible' | 'hidden' | 'locked'>(
    buttonVisibility || 'hidden'
  );

  // tracks full page scroll on mobile
  const scrollRef = useRef<HTMLDivElement>(null);

  // tracks properties column scroll on desktop
  const propertiesScrollRef = useRef<HTMLDivElement>(null);

  const citOrigin = useCitOriginClient();

  useEffect(() => {
    if (selectedReference) {
      setPrevButtonVisibility(buttonVisibility ?? 'hidden');
      setButtonVisibility?.('locked');
    } else {
      setButtonVisibility?.(prevButtonVisibility);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReference, setButtonVisibility, setPrevButtonVisibility]);

  const { accountTheme } = useCitThemeClient();

  const thumbnails = profile?.media;
  const hasMedia = thumbnails && thumbnails.length > 0;

  // the theme getter, so an origin that hides the name for privacy hides it here too
  const accountName = accountTheme.accountName();

  const bgColor = accountTheme.backgroundColor(profile ?? undefined, true);
  const bodyFontColor = accountTheme.bodyFontColor();
  const titleFontColor = accountTheme.titleFontColor();

  const bodyFont = accountTheme.bodyFont();
  const titleFont = accountTheme.titleFont();

  const lineHeight = accountTheme.lineHeight();
  const letterSpacing = accountTheme.letterSpacing();

  useEffect(() => {
    let lastScrollTop = 0;
    let buttonsShouldBeVisible = false;

    const handleScroll = () => {
      if (!scrollRef.current || !setButtonVisibility) {
        return;
      }

      const element = scrollRef.current;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      // Check if we're at the bottom (with a small tolerance for rounding errors)
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 1;

      const isScrollingUp = scrollTop < lastScrollTop;
      const isScrollingDown = scrollTop > lastScrollTop;

      // Show buttons when scrolling up or at the bottom
      if (isScrollingUp || isAtBottom) {
        buttonsShouldBeVisible = true;
      }

      // Hide buttons when scrolling down (unless at bottom)
      if (isScrollingDown && !isAtBottom) {
        buttonsShouldBeVisible = false;
      }

      setButtonVisibility(buttonsShouldBeVisible ? 'visible' : 'hidden');

      // Update lastScrollTop for next comparison
      lastScrollTop = scrollTop;
    };
    const pageScroll = scrollRef.current;
    const propertiesScroll = propertiesScrollRef.current;

    if (pageScroll && propertiesScroll && setButtonVisibility) {
      pageScroll.addEventListener('scroll', handleScroll);

      // Set initial opacity with longer delay to ensure layout is complete
      const initialOpacityTimeout = window.setTimeout(handleScroll, 400);

      // Also use ResizeObserver to handle content changes
      const resizeObserver = new ResizeObserver(() => {
        handleScroll();
      });
      resizeObserver.observe(pageScroll);

      return () => {
        window.clearTimeout(initialOpacityTimeout);
        pageScroll.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();

        // TODO: possibly will need to apply IOS scroll fix to this as well
        // reset scroll position to top for full page on mobile and properties column on desktop on unmount
        window.setTimeout(() => {
          pageScroll.scrollTo({ top: 0, behavior: 'instant' });
          propertiesScroll.scrollTo({ top: 0, behavior: 'instant' });
        }, 600);
      };
    }
  }, [setButtonVisibility, scrollRef]);

  // Prevent scroll on scrollable div when content is too short
  useEffect(() => {
    const div = scrollRef.current;
    if (!div) {
      return;
    }

    const handleTouchMove = (e: TouchEvent) => {
      // Only prevent if the div itself can't scroll
      if (div.scrollHeight <= div.clientHeight) {
        e.preventDefault();
      }
    };

    div.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      div.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  return (
    <div className='profile-detail-and-profile-overlay-container relative w-full h-full flex flex-col'>
      <div
        className='profile-detail-scroll-container flex flex-col h-full w-full pl-3 gap-6 overflow-y-auto lg:overflow-y-hidden overscroll-contain'
        style={{
          backgroundColor: bgColor,
          color: bodyFontColor,
          fontFamily: bodyFont
        }}
        // don't track scroll position if reference overlay is open
        ref={buttonVisibility !== 'locked' && !selectedReference ? scrollRef : undefined}
      >
        {/* Title Section */}
        <div className='profile-detail-title-section flex flex-col w-full gap-1 px-3 pt-4'>
          <div className='flex justify-between items-center overflow-hidden'>
            {/* when profile is an overlay make title clickable to open the profile in a full page view */}
            {/* purposefully using <a> instead of <Link> to prevent interceptor route from being triggered */}
            {isOverlay && profile ? (
              <a
                href={addTestThemeToHref(
                  getProfileInstanceLink(
                    profile.type,
                    toUrlSafeSlug(profile.slug),
                    citOrigin,
                    accountId,
                    true
                  ),
                  accountTheme.isTestTheme() ? accountTheme.getCompressedThemeConfig() : undefined
                )}
              >
                <div className='flex items-center gap-2'>
                  <h1
                    className='text-3xl font-bold hover:underline cursor-pointer line-clamp-1 break-all'
                    style={{
                      fontFamily: titleFont,
                      color: titleFontColor,
                      lineHeight,
                      letterSpacing
                    }}
                  >
                    {profile ? getName(profile.properties).replace(/"/g, '') : ''}
                  </h1>
                  <LinkIcon width={24} height={24} />
                </div>
              </a>
            ) : (
              <h1
                className='text-3xl font-bold line-clamp-1 break-all'
                style={{
                  fontFamily: titleFont,
                  color: titleFontColor,
                  lineHeight,
                  letterSpacing
                }}
              >
                {profile ? getName(profile.properties).replace(/"/g, '') : ''}
              </h1>
            )}

            {/* the modal's or the reference overlay's close button, top right */}
            {closeButton}
          </div>

          <div className='flex flex-row gap-2 items-center'>
            <h2 style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
              {profile?.type_label}
            </h2>
            {citOrigin !== CitOrigin.IFRAME ? (
              <>
                <span style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>-</span>
                <Link
                  href={addTestThemeToHref(
                    `/${citOrigin !== CitOrigin.HUBPRO ? profile?.account_slug || profile?.account_id : ''}`,
                    accountTheme.isTestTheme() ? accountTheme.getCompressedThemeConfig() : undefined
                  )}
                  prefetch={false}
                >
                  <h3
                    style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}
                    className='truncate underline'
                  >
                    {accountName}
                  </h3>
                </Link>
              </>
            ) : null}
          </div>
        </div>

        {/* Media & Properties Block */}
        <div className='profile-detail-media-and-properties-container flex flex-col lg:flex-row w-full lg:h-full p-3 gap-6 lg:overflow-y-hidden'>
          <div
            className={`profile-detail-properties-container flex flex-col w-full lg:w-5/12 lg:h-full lg:overflow-y-auto gap-4 lg:pb-0 overscroll-contain ${
              hasMedia ? '' : 'lg:w-full'
            }`}
            ref={propertiesScrollRef}
          >
            <PropertiesView
              properties={profile?.properties}
              accountId={profile?.account_slug || profile?.account_id || accountId}
              setSelectedReference={!isOverlay ? setSelectedReference : undefined}
              // references the current profile in the layout not the reference overlay, used so it won't link to itself
              currentReference={
                profile
                  ? { referenceLink: profile.slug || profile.id, referenceType: 'profile' }
                  : undefined
              }
            />
          </div>

          {hasMedia ? (
            <div className='profile-detail-media-container flex flex-col w-full lg:w-7/12 lg:h-full lg:overflow-y-hidden'>
              <MediaViewer
                detailId={profile?.slug ?? profile?.id ?? 'unknown'}
                thumbnails={thumbnails}
                enableGallery={enableGallery && !selectedReference}
                onScreen={enableGallery}
                accountId={profile?.account_slug || profile?.account_id || accountId}
              />
            </div>
          ) : null}
        </div>
      </div>
      <ReferenceOverlay
        selectedReference={selectedReference}
        setSelectedReference={setSelectedReference}
        accountId={accountId}
      />
    </div>
  );
}
